import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import queryString from 'querystring';

import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import { bemGenerator } from '@prisjakt/ui-base';
import { useAppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import LayoutPicker from '@prisjakt/ui-layout-picker';
import Dropdown from '@prisjakt/ui-dropdown';
import Breadcrumb from '@prisjakt/ui-breadcrumb';

import FilterIcon from 'mdi-react/FilterVariantIcon';

import MatchMedia from '../../components/match-media';
import PageHeader from '../../components/page-header';
import CampaignDeals from '../../components/campaign-deals';
import SubCategoryListing from './components/sub-category-listing';
import VisibilityToggle from './components/sub-category-listing/components/toggle';
import ProductListing from './components/product-listing';
import { HorizontalFilter } from './components/filter';
import FilterMobileToggle from './components/filter/components/mobile-toggle';
import SelectedFilters from './components/filter/components/selected-filters';
import PageContent from '../../components/page-content';
import CategoryInformation from './components/category-information';
import FeaturedArticle from '../../components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import NewProductReleases from '../../components/new-product-releases';
import AdContainer from '../../components/ads';
import Counter from './components/filter/components/counter';
import NoScroll from '../../components/no-scroll';

import { CategoryFragment } from '../../types';

import { CategoryList } from '../../components/category-list';
import Collapsed from '../../components/collapsed';
import CompareProductsBasket from '../../components/compare-products-basket';
import CompareProductsToggle from '../../components/compare-products-toggle';
import { useCompareProductsSelection } from '../../hooks/compare-products-selection';

import sidebarFilterActions from '../sidebar/actions/filter';
import { setHorizontalFilter as setHorizontalFilterAction } from './actions/filter';
import { categoryLayoutRequest as categoryLayoutRequestAction } from './actions/category';
import { sortChange, adsViewUpdate } from '../../globals/actions';

import { SIDEBAR_STATE } from '../sidebar/constants';

import * as S from './styles';
import './index.scss';
import './sagas/index';

const styles = bemGenerator('category-page', [
  'wrapper',
  'gutter',
  'product-wrapper',
  'header',
  'header-info',
  'sub-header',
  'header-article-link',
  'categories',
  'product-count',
  'products',
  'products-header-buttons',
  'articles',
  'article',
  'dailyDeals',
  'buttons',
  'filter',
  'media',
  'description',
  'aliases',
  'selected-filters',
  'classic-notice',
  'expand-description-button',
  'adWrapper',
]);

const isVirtualCategory = categoryId => /^v/.test(categoryId);
const sortOptions = ['name', 'popularity', 'user_reviews'];
const bothDirectionsSortOptions = ['price', 'price_include_shipping'];

const Category = props => {
  const [subCategoriesCollapsed, setSubCategoriesCollapsed] = useState(true);
  const toggleSubCategoriesCollapsed = useCallback(
    () => setSubCategoriesCollapsed(!subCategoriesCollapsed),
    [subCategoriesCollapsed]
  );
  const { t, linkTo } = useAppContext();
  const {
    isSelectingProducts,
    selectedProducts,
    startProductSelection,
    abortProductSelection,
    selectProduct,
    deselectProduct,
  } = useCompareProductsSelection();

  const {
    categorySettings,
    sidebarSettings,
    propertySummaryMode,
    showHorizontalFilter,
    history,
    categoryLayoutRequest,
    setHorizontalFilter,
  } = props;
  const {
    id,
    immediateProducts,
    products,
    categories,
    filterId,
    brand,
    brands,
    name,
    aliases,
    description,
    logo,
    path,
    pathName,
    defaultLayoutMode,
    productCount,
    storesCount,
    deals,
    newReleases,
    preReleases,
    categoryTree,
    advized,
    articles,
  } = props.data.category;

  const abortProductSelectionFromCompareButton = useCallback(
    () => abortProductSelection('compareButton'),
    [abortProductSelection]
  );
  const abortProductSelectionFromBasket = useCallback(
    () => abortProductSelection('basket'),
    [abortProductSelection]
  );

  const selectProductById = useCallback(
    (productId, source) => {
      const selectedProduct = products.nodes.find(p => p.id === productId);
      if (selectedProduct) {
        selectProduct(source, selectedProduct);
      }
    },
    [products, selectProduct]
  );

  const deselectProductById = useCallback(
    (productId, source) => {
      const deselectedProduct = products.nodes.find(p => p.id === productId);
      if (deselectedProduct) {
        deselectProduct(source, deselectedProduct);
      }
    },
    [products, deselectProduct]
  );

  useEffect(() => {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    props.adsViewUpdate('category:mounted');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { pageInfo } = props.data.category.products;

  const dropdownSortOptions = useMemo(() => {
    const bothDirectionsOptions = bothDirectionsSortOptions
      .filter(x => pageInfo.sort.available.includes(x))
      .reduce(
        (arr, option) => [
          {
            name: t('common', `sort.${option}:asc`),
            value: `${option}:asc`,
          },
          {
            name: t('common', `sort.${option}:desc`),
            value: `${option}:desc`,
          },
          ...arr,
        ],
        []
      )
      .filter(
        x => x.value !== `${pageInfo.sort.name}:${pageInfo.sort.direction}`
      );

    return sortOptions
      .filter(x => pageInfo.sort.available.includes(x))
      .filter(
        x => x !== pageInfo.sort.name && !bothDirectionsSortOptions.includes(x)
      )
      .map(option => ({
        name: t('common', `sort.${option}`),
        value: `${option}`,
      }))
      .concat(bothDirectionsOptions);
  }, [t, pageInfo.sort]);

  const dropdownSortSelected = useMemo(() => {
    return pageInfo.sort.name.indexOf('property:') !== 0
      ? t(
          'common',
          `sort.${pageInfo.sort.name}${
            bothDirectionsSortOptions.includes(pageInfo.sort.name)
              ? `:${pageInfo.sort.direction}`
              : ''
          }`
        )
      : '';
  }, [t, pageInfo.sort]);

  function onSortChange(value) {
    const location = history.location;
    const qs = queryString.decode(location.search.substr(1));

    const selectedSortProperty = value.replace(/:(asc|desc)$/, '');
    const selectedSortDirection = value.match(/:(asc|desc)$/);

    const currentSortDirection = pageInfo.sort.direction;

    const data = { replaceHistory: true };

    if (selectedSortDirection) {
      // Sort direction was selected from dropdown
      data.value = `${selectedSortProperty}:${selectedSortDirection[1]}`;
    } else if (qs.sort === selectedSortProperty) {
      // Same sort property, then we toggle the current sort direction
      data.value = `${selectedSortProperty}:${
        currentSortDirection === 'asc' ? 'desc' : 'asc'
      }`;
    } else {
      // Changed to a new sort property
      data.value = `${selectedSortProperty}`;
    }

    delete qs.offset;

    props.sortChange(data);
  }

  function getListRenderMode(mode, isDesktop) {
    // Render row mode as a table if category has filter and everything
    if (mode === 'row' && immediateProducts && isDesktop) {
      return 'table';
    }

    return mode;
  }

  function renderHeader(mode, matchMedia) {
    const { isMobile } = matchMedia;

    const productCountText = t('category', 'products.count', {
      n: productCount,
    });
    const headerTitle = isMobile ? '' : t('category', 'products.title');
    const headerSubtitle = isMobile ? '' : productCountText;
    const { error: filterError, query } = categorySettings.filter;
    const count = Object.keys(query).length;
    const buttonColor = 'primary';
    const filterIsOpen =
      sidebarSettings && sidebarSettings.tab === SIDEBAR_STATE.FILTER;

    return (
      <>
        {immediateProducts ? (
          <S.PanelHeaderWrapper>
            <S.PanelHeader
              title={headerTitle}
              subtitle={headerSubtitle}
              divider={isMobile ? 'both' : 'none'}
            >
              {!filterError && !showHorizontalFilter && (
                <S.FilterButton
                  color={buttonColor}
                  variant={filterIsOpen ? 'default' : 'outlined'}
                  onClick={() => props.toggleFilter()}
                  icon={
                    count ? (
                      <Counter
                        count={count}
                        variant={filterIsOpen ? 'light' : 'dark'}
                        color={buttonColor}
                      />
                    ) : (
                      <FilterIcon size="18" />
                    )
                  }
                >
                  {t('filter', 'filter.title')}
                </S.FilterButton>
              )}
              <Dropdown
                title={isMobile ? '' : t('common', 'sort.title')}
                options={dropdownSortOptions}
                selected={dropdownSortSelected}
                onSelect={option => {
                  onSortChange(option.value);
                }}
                data-test="SortOrderDropdown"
              />
              <div className={styles['products-header-buttons']}>
                <LayoutPicker mode={mode} onClick={categoryLayoutRequest} />
                <CompareProductsToggle
                  active={isSelectingProducts}
                  activate={startProductSelection}
                  deactivate={abortProductSelectionFromCompareButton}
                />
              </div>
            </S.PanelHeader>
          </S.PanelHeaderWrapper>
        ) : (
          <PanelHeader
            title={t('category', 'popular-products.title', {
              categoryName: name,
            })}
            divider="none"
          />
        )}

        <CategoryInformation
          statistics={{
            productsCount: productCount,
            storesCount,
          }}
        />

        {immediateProducts ? (
          <div className={styles['product-count']}>{productCountText}</div>
        ) : null}
      </>
    );
  }

  const sortedProducts = useMemo(() => {
    if (!products || immediateProducts) {
      return products;
    }

    // For non-leaf categories, we want to re-sort the fetched products to showcase more sub-categories,
    // and only render the first 12
    return {
      ...products,
      nodes: products.nodes
        ? products.nodes
            .sort((a, b) => {
              if (a.popularity.inCategory === b.popularity.inCategory) {
                return a.popularity.total > b.popularity.total ? 1 : -1;
              }

              return a.popularity.inCategory > b.popularity.inCategory ? 1 : -1;
            })
            .slice(0, 12)
        : products.nodes,
    };
  }, [products, immediateProducts]);

  const productListingAdverts = useMemo(
    () => ({
      desktopInline: (
        <AdContainer
          scalable
          scaleBase=".pj-ui-panel"
          scaleOrigin="left center"
          min={861}
          max={10000}
          spacing={{ bottom: '10px' }}
          {...(immediateProducts
            ? {
                placement: 'panorama_extra',
                format: 'panorama_1_wde',
              }
            : {
                placement: 'panorama_target',
                format: 'panorama_3_wde',
                triggerPoint: 100,
              })}
        />
      ),
    }),
    [immediateProducts]
  );

  function renderProductListing(matchMedia) {
    const { isDesktop } = matchMedia;
    const { comparing, error: filterError } = categorySettings.filter;
    const showFilter = immediateProducts && !filterError;
    const selectedProperties = products.pageInfo.selectedProperties.filter(x =>
      comparing.includes(x.id)
    );

    const mode =
      (!immediateProducts && 'grid') ||
      (categorySettings.category && categorySettings.category.mode) ||
      defaultLayoutMode ||
      'grid';
    return (
      <ProductListing
        header={renderHeader(mode, matchMedia)}
        advized={advized}
        products={sortedProducts}
        selectedProperties={selectedProperties}
        mode={getListRenderMode(mode, isDesktop)}
        pageInfo={products.pageInfo}
        withFilter={showFilter}
        activeCampaign={props.activeCampaign}
        propertySummaryMode={propertySummaryMode}
        onSortChange={onSortChange}
        fallbackImage={logo}
        showPagination={immediateProducts}
        showRatings={immediateProducts}
        showCategory={!immediateProducts}
        showProductSelector={immediateProducts && isSelectingProducts}
        selectedProducts={selectedProducts}
        onProductSelect={selectProductById}
        onProductDeselect={deselectProductById}
        adverts={productListingAdverts}
      />
    );
  }

  function renderArticles() {
    const featuredArticle = articles
      ? articles.find(item => item.featured)
      : null;
    if (!articles || articles.length === 0) {
      return null;
    }
    return (
      <div className={`${styles.gutter} ${styles.articles}`}>
        <Panel
          header={
            <PanelHeader
              title={t('category', 'article.related-articles', {
                categoryName: name,
              })}
            />
          }
        >
          <FeaturedArticle article={featuredArticle} related={articles} />
        </Panel>
      </div>
    );
  }

  const toggleFilter = useCallback(() => {
    setHorizontalFilter(!showHorizontalFilter);
  }, [setHorizontalFilter, showHorizontalFilter]);

  const mode =
    (!immediateProducts && 'grid') ||
    (categorySettings.category && categorySettings.category.mode) ||
    defaultLayoutMode;

  const { mobileOpen, query, error: filterError } = categorySettings.filter;

  const hasSubCategories = categories && categories.nodes.length > 0;
  const hasSubBrandCategories =
    !isVirtualCategory(id) && brands && brands.nodes.length > 0;
  const brandCategories = hasSubBrandCategories
    ? brands.nodes.map(categoryBrand => ({
        id: `${id}:${categoryBrand.id}`,
        name: `${categoryBrand.name} ${name}`,
        pathName: linkTo('brandCategory', {
          id,
          pathName,
          brandId: categoryBrand.id,
        }),
      }))
    : [];

  const campaignPromoBanners = {
    before:
      props.campaignBanners &&
      props.campaignBanners.active.filter(
        b => b && b.type === 'BEFORE_PROMO_BANNER'
      ),
    during:
      props.campaignBanners &&
      props.campaignBanners.active.filter(
        b => b && b.type === 'DURING_PROMO_BANNER'
      ),
  };
  const campaignBannerLinkParams = { category: id };

  const showFilter = immediateProducts && !filterError;

  return (
    <MatchMedia
      debounce={150}
      serverSideRender={{ isDesktop: true }}
      matches={[
        {
          mediaQuery: '(max-width: 640px)',
          values: { isMobile: true },
        },
        {
          mediaQuery: '(min-width: 641px) and (max-width: 1023px)',
          values: { isTablet: true },
        },
        {
          mediaQuery: '(min-width: 1024px)',
          values: { isDesktop: true },
        },
      ]}
    >
      {matchMedia => {
        const { isDesktop } = matchMedia;
        return (
          <div
            className={styles.block.withModifier({
              'mobile-open': mobileOpen,
            })}
          >
            <PageContent withAds data-test="CategoryPage">
              {campaignPromoBanners.during &&
                campaignPromoBanners.during.length > 0 && (
                  <div className={styles.gutter}>
                    <PromoBanner
                      banners={campaignPromoBanners.during}
                      linkParams={campaignBannerLinkParams}
                    />
                  </div>
                )}
              <Breadcrumb
                homeText={t('common', 'breadcrumb.home')}
                path={path}
              />

              <div className={styles.wrapper}>
                <div className={styles.header}>
                  <PageHeader
                    className={styles['header-info']}
                    media={
                      logo && (
                        <img
                          className={styles.media}
                          width={100}
                          alt={name}
                          src={logo}
                        />
                      )
                    }
                    title={name}
                    right={
                      brand && brand.featured && brand.logo ? (
                        <S.BrandLink to={linkTo('brand', brand)}>
                          <S.BrandLogo src={brand.logo} alt={brand.name} />
                        </S.BrandLink>
                      ) : null
                    }
                  >
                    {/* Don't show the category description on brand category pages */}
                    {!brand && (
                      <Collapsed mobile>
                        <div
                          className={styles.description}
                          data-test="CategoryDescription"
                        >
                          <span
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                          {aliases && aliases.length > 0 && (
                            <div className={styles.aliases}>
                              {t('category', 'category.alias', {
                                alias: aliases.join(', '),
                              })}
                            </div>
                          )}
                        </div>
                      </Collapsed>
                    )}
                  </PageHeader>
                </div>

                {(hasSubCategories || hasSubBrandCategories) &&
                  isDesktop &&
                  immediateProducts && (
                    <div className={styles.categories}>
                      <div className={styles.gutter}>
                        <SubCategoryListing
                          styling="inline-list"
                          categories={categories}
                          brands={brandCategories}
                        />
                      </div>
                    </div>
                  )}

                {hasSubCategories && !immediateProducts && categoryTree && (
                  <div className={styles.categories}>
                    <div className={styles.gutter}>
                      <>
                        <CategoryList categories={categoryTree} withAds />

                        <div className={styles.adWrapper}>
                          <AdContainer
                            scalable
                            scaleBase=".category-page--adWrapper"
                            placement="panorama_extra"
                            format="panorama_1_wde"
                            min={861}
                            max={10000}
                            spacing={{ top: '0', bottom: '10px' }}
                          />
                        </div>
                      </>
                    </div>
                  </div>
                )}

                {showFilter && !mobileOpen && (
                  <div className={styles['sub-header']}>
                    <S.Column fullWidth={!hasSubCategories}>
                      <FilterMobileToggle filterSettings={query} />
                    </S.Column>
                    {!isDesktop && hasSubCategories && (
                      <>
                        <S.Column>
                          <VisibilityToggle
                            active={!subCategoriesCollapsed}
                            onToggle={toggleSubCategoriesCollapsed}
                            label={t('category', 'subcategories.show')}
                          />
                        </S.Column>
                        <S.Column fullWidth>
                          <SubCategoryListing
                            styling="list"
                            categories={categories}
                            collapse={subCategoriesCollapsed}
                          />
                        </S.Column>
                      </>
                    )}
                    <S.Column fullWidth>
                      <SelectedFilters
                        categoryId={id}
                        className={styles['selected-filters']}
                      />
                    </S.Column>
                  </div>
                )}

                <>
                  {showFilter && isDesktop && props.showHorizontalFilter && (
                    <HorizontalFilter
                      categoryId={id}
                      filterId={filterId}
                      brand={brand}
                      productCount={productCount}
                      canCompare={mode === 'row'}
                      quickFilters={[]}
                      className="column"
                      toggleFilter={toggleFilter}
                      expanded={props.showHorizontalFilter}
                    />
                  )}

                  {(products.nodes.length > 0 || immediateProducts) && (
                    <div className={styles.gutter}>
                      <div className={styles['product-wrapper']}>
                        {showFilter && !isDesktop && (
                          <>
                            <NoScroll mobile enabled={mobileOpen} />
                            <S.Filter
                              open={mobileOpen}
                              categoryId={id}
                              filterId={filterId}
                              brand={brand}
                              productCount={productCount}
                              canCompare={mode === 'row'}
                              quickFilters={[]}
                              showFilterToggle={isDesktop}
                              toggleFilter={toggleFilter}
                              expanded={props.showHorizontalFilter}
                            />
                          </>
                        )}
                        <div
                          className={styles.products}
                          data-test="ProductListing"
                          data-test-category={id}
                        >
                          {renderProductListing(matchMedia)}
                          {isSelectingProducts && (
                            <CompareProductsBasket
                              products={selectedProducts}
                              compareUrl={
                                selectedProducts.length > 1
                                  ? linkTo('productCompare', {
                                      category: props.data.category,
                                      products: selectedProducts,
                                    })
                                  : undefined
                              }
                              onEmpty={abortProductSelectionFromBasket}
                              onProductDelete={deselectProductById}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>

                {campaignPromoBanners.before &&
                  campaignPromoBanners.before.length > 0 && (
                    <div className={styles.gutter}>
                      <PromoBanner
                        inverted
                        banners={campaignPromoBanners.before}
                      />
                    </div>
                  )}

                {renderArticles()}
                {deals && deals.length > 0 && (
                  <div className={`${styles.gutter} ${styles.dailyDeals}`}>
                    <CampaignDeals
                      deals={deals}
                      campaignLinkQuery={`?category=${id}`}
                    />
                  </div>
                )}

                <div className={styles.gutter}>
                  <NewProductReleases
                    newReleases={
                      newReleases && newReleases.nodes.length > 3
                        ? newReleases
                        : undefined
                    }
                    preReleases={
                      preReleases && preReleases.nodes.length > 3
                        ? preReleases
                        : undefined
                    }
                    personalized={false}
                  />

                  <RecentlyVisitedProducts />
                </div>
              </div>
            </PageContent>
          </div>
        );
      }}
    </MatchMedia>
  );
};

const { arrayOf, bool, func, number, object, oneOf, shape, string } = PropTypes;

Category.propTypes = {
  data: shape({
    category: graphqlPropTypes(CategoryFragment),
  }).isRequired,
  categorySettings: shape({
    category: shape({
      mode: string,
    }),
    filter: shape({
      mobileOpen: bool.isRequired,
    }),
  }).isRequired,
  categoryLayoutRequest: func.isRequired,
  categorySortRequest: func,
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        type: string,
        url: string,
        imageUrl: string,
      })
    ),
  }),
  propertySummaryMode: string,
  brand: number,
  defaultLayoutMode: oneOf(['grid', 'row']),
  campaignBanners: shape({
    active: arrayOf(object),
  }),
  adsViewUpdate: func.isRequired,
  sortChange: func.isRequired,
  toggleFilter: func.isRequired,
  sidebarSettings: shape({
    tab: string,
  }),
  showHorizontalFilter: bool,
  setHorizontalFilter: func,
};

Category.defaultProps = {
  categorySortRequest: () => {},
  activeCampaign: undefined,
  propertySummaryMode: 'verbose',
  brand: null,
  defaultLayoutMode: 'grid',
  campaignBanners: undefined,
  sidebarSettings: {},
  showHorizontalFilter: false,
  setHorizontalFilter: () => {},
};

const mapStateToProps = state => ({
  categorySettings: state.areas.category,
  campaignBanners: state.globals.campaignBanners,
  sidebarSettings: state.areas.sidebar,
  showHorizontalFilter: state.areas.category.filter.showHorizontalFilter,
});

const dispatchProps = {
  toggleFilter: sidebarFilterActions.toggleFilter,
  sortChange,
  adsViewUpdate,
  categoryLayoutRequest: categoryLayoutRequestAction,
  setHorizontalFilter: setHorizontalFilterAction,
};

export default connect(mapStateToProps, dispatchProps)(withRouter(Category));
